import { Tag } from "primereact/tag";
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

import { parseDate } from "../../helpers/date";
import { isNoMaximumOOP } from "../../helpers/price";
import { PatientBenefits } from "../../pages/CreateOrder/components/PatientBenefitsSection/components/Benefits/components/PatientBenefits";
import {
  IClaim,
  ILineItem,
  IMatchedOrder,
  IVarianceAnalysis,
} from "../../types/claim";
import {
  calculateOOPPercent,
  extractClaimPatientResponsibility,
  formatCurrency,
  formatPercent,
  generateEstimateCaseSignature,
  isImplantCptCode,
} from "../../utils/claimAdjustments";

interface IEstimateComparisonTabProps {
  claim: IClaim;
}

const Container = styled.div`
  padding: 1rem;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
`;

const MatchInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const MatchLabel = styled.span`
  font-weight: 600;
  color: #495057;
`;

const OrderDate = styled.span`
  color: #6c757d;
  font-size: 0.9rem;
`;

const OrderLink = styled(Link)`
  color: #007bff;
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const NoMatchMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: #6c757d;
`;

const NoMatchIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  color: #dee2e6;
`;

const ComparisonTable = styled.table`
  width: auto;
  max-width: 800px;
  border-collapse: collapse;
  margin-bottom: 2rem;

  th,
  td {
    padding: 0.5rem 1.5rem;
    text-align: right;
    border-bottom: 1px solid #e9ecef;
  }

  th:first-child,
  td:first-child {
    text-align: left;
    padding-left: 0;
  }

  th:last-child,
  td:last-child {
    padding-right: 0;
  }

  th {
    background-color: #f8f9fa;
    font-weight: 600;
    color: #495057;
  }

  tr:hover {
    background-color: #f8f9fa;
  }
`;

const VarianceCell = styled.td<{
  $isPositive?: boolean;
  $isNegative?: boolean;
}>`
  color: ${(props) => {
    if (props.$isPositive) return "#28a745";
    if (props.$isNegative) return "#dc3545";
    return "#495057";
  }};
  font-weight: ${(props) =>
    props.$isPositive || props.$isNegative ? "600" : "400"};
`;

const SectionTitle = styled.h4`
  color: #495057;
  margin: 1.5rem 0 1rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #007bff;
  font-size: 1rem;
`;

const EligibilityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const EligibilityCard = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
`;

const EligibilityLabel = styled.div`
  font-size: 0.85rem;
  color: #6c757d;
  margin-bottom: 0.25rem;
`;

const EligibilityValue = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: #212529;
`;

const ClaimOnlySection = styled.div`
  margin-top: 1.5rem;
`;

const MismatchHighlight = styled.span`
  color: #dc3545;
  font-weight: 600;
`;

const QuantityMismatch = styled.span`
  color: #dc3545;
  font-weight: 600;
`;

const ExpectedAmountNote = styled.span`
  color: #6c757d;
  font-size: 0.85em;
`;

const TableFootnote = styled.div`
  font-size: 0.9rem;
  color: #6c757d;
  margin-top: -1rem;
  margin-bottom: 1rem;
`;

const TwoColumnLayout = styled.div`
  display: flex;
  gap: 2rem;
  align-items: flex-start;

  @media (max-width: 1600px) {
    flex-direction: column;
  }
`;

const LeftColumn = styled.div`
  flex: 0 0 auto;
`;

const RightColumn = styled.div`
  flex: 1;
  min-width: 0;
`;

const BenefitsSection = styled.div`
  /* Override PatientBenefits outer container - remove width restriction and center alignment */
  & > div.flex.flex-row {
    width: 100% !important;
    justify-content: flex-start !important;
    align-items: flex-start !important;
  }

  /* Hide the vertical line divider not needed in this context */
  & .border-left-1 {
    display: none !important;
  }

  /* Ensure the inner container takes full width */
  & > div > div {
    width: 100% !important;
    padding-top: 0 !important;
  }

  /* Fix tab nav scrolling - prevent vertical overflow */
  & .p-tabview-nav {
    overflow-x: auto;
    overflow-y: hidden;
    flex-wrap: nowrap !important;
  }

  & .p-tabview-nav-content {
    overflow-y: hidden;
  }

  /* Left-align the tab view */
  & .p-tabview {
    width: 100%;
  }
`;

const RootCausesSummary = styled.div`
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 6px;
  padding: 1rem 1.25rem;
  margin-bottom: 1.5rem;
`;

const RootCausesHeader = styled.div`
  font-weight: 600;
  color: #856404;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const RootCausesList = styled.ul`
  margin: 0;
  padding-left: 1.25rem;
  color: #856404;
`;

const RootCauseItem = styled.li`
  margin: 0.25rem 0;
  font-weight: 500;
`;

const RootCauseDetail = styled.span`
  font-weight: 400;
  color: #6c757d;
`;

/**
 * Format root causes from variance analysis for display
 * Returns an array of formatted root cause descriptions
 */
export function formatRootCauses(
  analysis: IVarianceAnalysis | null | undefined,
  lineItems?: ILineItem[]
): { label: string; detail: string }[] {
  if (!analysis?.hasDiscrepancy) return [];

  const causes: { label: string; detail: string }[] = [];
  const { rootCauses } = analysis;

  // Allowable Mismatch
  if (rootCauses.allowableMismatch?.detected) {
    const { type, variance } = rootCauses.allowableMismatch;
    const amount = formatCurrency(Math.abs(variance));
    const direction = type === "claim_high" ? "higher" : "lower";
    causes.push({
      label: "Allowable Mismatch",
      detail: `Claim is ${amount} ${direction} than expected`,
    });
  }

  // Code Change
  if (rootCauses.codeChange?.detected) {
    const { codesAdded, codesDropped, quantityChanges } = rootCauses.codeChange;
    const parts: string[] = [];
    if (codesAdded.length > 0) {
      const codeList = codesAdded.join(", ");
      const verb = codesAdded.length === 1 ? "was" : "were";
      parts.push(`${codeList} ${verb} billed but not estimated`);
    }
    if (codesDropped.length > 0) {
      const codeList = codesDropped.join(", ");
      const verb = codesDropped.length === 1 ? "was" : "were";
      parts.push(`${codeList} ${verb} estimated but not billed`);
    }
    if (quantityChanges && quantityChanges.length > 0) {
      const qtyDetails = quantityChanges
        .map((qc) => `${qc.code} qty: ${qc.estimateQty} → ${qc.claimQty}`)
        .join(", ");
      parts.push(qtyDetails);
    }
    causes.push({
      label: "Code Change",
      detail: parts.join("; "),
    });
  }

  // Implant PT Responsibility
  if (rootCauses.implantPtResponsibility?.detected) {
    const { implantsInClaimOnly, implantsInEstimateOnly } =
      rootCauses.implantPtResponsibility;
    const parts: string[] = [];
    if (implantsInClaimOnly.length > 0) {
      // Look up patient responsibility amounts for implant codes from line items
      const implantDetails = implantsInClaimOnly.map((code) => {
        const lineItem = lineItems?.find((li) => li.cptCode === code);
        const amount = lineItem?.patientResponsibility;
        return amount && amount > 0
          ? `${code} amount $${amount.toFixed(2)}`
          : code;
      });
      parts.push(`in claim only: ${implantDetails.join(", ")}`);
    }
    if (implantsInEstimateOnly.length > 0) {
      parts.push(`in estimate only: ${implantsInEstimateOnly.join(", ")}`);
    }
    causes.push({
      label: "Implant Discrepancy",
      detail: parts.join("; "),
    });
  }

  // Benefits Mismatch
  if (rootCauses.benefitsMismatch?.detected) {
    const details: string[] = [];
    if (rootCauses.benefitsMismatch.deductible?.detected) {
      details.push("Deductible");
    }
    if (rootCauses.benefitsMismatch.coinsurancePercent?.detected) {
      details.push("Coinsurance %");
    }
    if (rootCauses.benefitsMismatch.oopMax?.detected) {
      details.push("OOP");
    }
    if (rootCauses.benefitsMismatch.copay?.detected) {
      details.push("Copay");
    }
    if (details.length > 0) {
      causes.push({
        label: "Benefits Mismatch",
        detail: details.join(", "),
      });
    }
  }

  // Payer Type Mismatch (MA vs Commercial)
  if (rootCauses.payerTypeMismatch?.detected) {
    const { mismatchType } = rootCauses.payerTypeMismatch;
    let detail = "";
    if (mismatchType === "claim_ma_estimate_commercial") {
      detail = "Estimate is for commercial, claim is MA";
    } else if (mismatchType === "claim_commercial_estimate_ma") {
      detail = "Estimate is for MA, claim is commercial";
    }
    causes.push({
      label: "MA and Commercial Mismatch",
      detail,
    });
  }

  return causes;
}

/**
 * Get match confidence tag severity and label
 */
function getConfidenceInfo(confidence: number | null | undefined): {
  severity: "success" | "warning" | "danger" | "info";
  label: string;
} {
  if (confidence === null || confidence === undefined) {
    return { severity: "info", label: "Unknown" };
  }

  if (confidence >= 0.9) {
    return {
      severity: "success",
      label: `High (${(confidence * 100).toFixed(0)}%)`,
    };
  }
  if (confidence >= 0.7) {
    return {
      severity: "warning",
      label: `Medium (${(confidence * 100).toFixed(0)}%)`,
    };
  }
  return {
    severity: "danger",
    label: `Low (${(confidence * 100).toFixed(0)}%)`,
  };
}

/**
 * Format percentage variance for display
 * Shows "-" for values close to zero, otherwise shows with +/- prefix
 */
function formatPercentVariance(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return "-";
  }
  if (Math.abs(value) < 0.05) {
    return "-";
  }
  const prefix = value > 0 ? "+" : "";
  return `${prefix}${value.toFixed(1)}%`;
}

/**
 * Format case signature variance
 * Shows "-" if signatures match or unavailable, "Code Change" if different
 */
function formatCaseSignatureVariance(match: boolean | null): string {
  if (match === null || match === true) {
    return "-";
  }
  return "Code Change";
}

/**
 * Parse a case signature string into a map of CPT code -> quantity
 */
function parseCaseSignature(signature: string): Map<string, number> {
  const result = new Map<string, number>();
  if (!signature) return result;

  signature.split(",").forEach((pair) => {
    const [cpt, qty] = pair.split(":");
    if (cpt && qty) {
      result.set(cpt, parseInt(qty, 10));
    }
  });

  return result;
}

/**
 * Group CPT code pairs into rows based on total count
 * - 1-3 codes: single row
 * - 4 codes: 2+2
 * - 5 codes: 3+2
 * - 6+: groups of 3
 */
function groupIntoCptRows<T>(items: T[]): T[][] {
  const total = items.length;
  if (total <= 3) return [items];
  if (total === 4) return [items.slice(0, 2), items.slice(2)];
  if (total === 5) return [items.slice(0, 3), items.slice(3)];

  // 6+: groups of 3
  const rows: T[][] = [];
  for (let i = 0; i < total; i += 3) {
    rows.push(items.slice(i, i + 3));
  }
  return rows;
}

/**
 * Render a case signature with highlighted differences
 * - CPT codes not in the other signature are fully highlighted in red
 * - CPT codes with quantity mismatches have only the quantity highlighted in red
 */
function renderCaseSignatureWithHighlights(
  signature: string,
  otherSignature: string
): React.ReactNode {
  if (!signature) return "-";

  const otherMap = parseCaseSignature(otherSignature);
  const pairs = signature.split(",");
  const needsHighlighting = otherSignature && signature !== otherSignature;

  // Render a single CPT pair with optional highlighting
  const renderPair = (pair: string, isLastInRow: boolean) => {
    const [cpt, qty] = pair.split(":");
    const otherQty = otherMap.get(cpt);
    const separator = isLastInRow ? "" : ", ";

    if (needsHighlighting && otherQty === undefined) {
      return (
        <React.Fragment key={cpt}>
          <MismatchHighlight>{pair}</MismatchHighlight>
          {separator}
        </React.Fragment>
      );
    }

    if (needsHighlighting && otherQty !== parseInt(qty, 10)) {
      return (
        <React.Fragment key={cpt}>
          {cpt}:<QuantityMismatch>{qty}</QuantityMismatch>
          {separator}
        </React.Fragment>
      );
    }

    return (
      <React.Fragment key={cpt}>
        {pair}
        {separator}
      </React.Fragment>
    );
  };

  const rows = groupIntoCptRows(pairs);

  // If only one row, render inline without wrapping divs
  if (rows.length === 1) {
    return rows[0].map((pair, idx) =>
      renderPair(pair, idx === rows[0].length - 1)
    );
  }

  // Multiple rows: wrap each in a div with nowrap to prevent mid-row breaks
  return rows.map((row, rowIdx) => (
    <div key={rowIdx} style={{ whiteSpace: "nowrap" }}>
      {row.map((pair, idx) => renderPair(pair, idx === row.length - 1))}
    </div>
  ));
}

/**
 * Extract estimate values from matched order
 * Gets values from pricingBreakdown which contains the actual estimate calculations
 * Only considers FACILITY procedures to match 835 claim scope
 *
 * Note: pricingBreakdown.totalInCents is the total patient OOP, NOT allowed amount
 * The allowed amount must be summed from procedureBreakdown[].amountInCents
 */
function extractEstimateValues(order: IMatchedOrder | null | undefined): {
  allowedAmount: number | null;
  deductible: number | null;
  coinsurance: number | null;
  copay: number | null;
  totalOOP: number | null;
} {
  if (!order) {
    return {
      allowedAmount: null,
      deductible: null,
      coinsurance: null,
      copay: null,
      totalOOP: null,
    };
  }

  const { gfeQuote } = order;
  const pricingBreakdown = gfeQuote?.pricingBreakdown as any;

  // Sum values from FACILITY procedures only (835 claims only contain facility data)
  // - amountInCents: the allowable amount per procedure
  // - owedByPatient: patient responsibility breakdown (deductible, coinsurance, copay, total)
  let allowedAmount: number | null = null;
  let deductible: number | null = null;
  let coinsurance: number | null = null;
  let copay: number | null = null;
  let totalOOP: number | null = null;

  if (pricingBreakdown?.procedureBreakdown?.length > 0) {
    // Filter to FACILITY procedures only
    const facilityProcedures = pricingBreakdown.procedureBreakdown.filter(
      (proc: any) => proc.specialty === "FACILITY"
    );

    if (facilityProcedures.length > 0) {
      const totals = facilityProcedures.reduce(
        (
          acc: {
            allowedAmount: number;
            deductible: number;
            coinsurance: number;
            copay: number;
            totalOOP: number;
          },
          procedure: any
        ) => {
          const { owedByPatient, amountInCents } = procedure;
          return {
            allowedAmount: acc.allowedAmount + (amountInCents ?? 0),
            deductible: acc.deductible + (owedByPatient?.deductible ?? 0),
            coinsurance: acc.coinsurance + (owedByPatient?.coinsurance ?? 0),
            copay: acc.copay + (owedByPatient?.copay ?? 0),
            totalOOP: acc.totalOOP + (owedByPatient?.total ?? 0),
          };
        },
        {
          allowedAmount: 0,
          deductible: 0,
          coinsurance: 0,
          copay: 0,
          totalOOP: 0,
        }
      );
      allowedAmount = totals.allowedAmount;
      deductible = totals.deductible;
      coinsurance = totals.coinsurance;
      copay = totals.copay;
      totalOOP = totals.totalOOP;
    }
  }

  return {
    allowedAmount,
    deductible,
    coinsurance,
    copay,
    totalOOP,
  };
}

/**
 * Extract selected benefit values from GfeQuote
 * These are the benefit values selected from PVerify eligibility response
 */
function extractSelectedBenefits(order: IMatchedOrder | null | undefined): {
  deductible: number | null;
  oopMax: number | null;
  copay: number | null;
  coinsurancePercent: number | null;
} {
  if (!order?.gfeQuote) {
    return {
      deductible: null,
      oopMax: null,
      copay: null,
      coinsurancePercent: null,
    };
  }

  const { gfeQuote } = order;

  return {
    deductible: gfeQuote.remainingDeductibleInCents ?? null,
    oopMax: gfeQuote.outOfPocketMaxInCents ?? null,
    copay: gfeQuote.flatCopayInCents ?? null,
    coinsurancePercent: gfeQuote.coinsurancePercent ?? null,
  };
}

export function EstimateComparisonTab({ claim }: IEstimateComparisonTabProps) {
  const { order } = claim;
  const hasMatch = !!order;

  // Extract claim-side values (returned in DOLLARS from 835 EDI)
  const claimResponsibilityDollars = useMemo(
    () => extractClaimPatientResponsibility(claim.lineItems),
    [claim.lineItems]
  );

  // Convert claim responsibility to cents for consistent comparison with estimate values
  const claimResponsibility = useMemo(
    () => ({
      deductible: claimResponsibilityDollars.deductible * 100,
      coinsurance: claimResponsibilityDollars.coinsurance * 100,
      copay: claimResponsibilityDollars.copay * 100,
      totalOOP: claimResponsibilityDollars.totalOOP * 100,
    }),
    [claimResponsibilityDollars]
  );

  // Extract estimate-side values (in cents) - FACILITY procedures only
  const estimateValues = useMemo(() => extractEstimateValues(order), [order]);

  // Extract selected benefit values from GfeQuote (PVerify eligibility response)
  const selectedBenefits = useMemo(
    () => extractSelectedBenefits(order),
    [order]
  );

  // Generate estimate case signature (only FACILITY procedures)
  const estimateCaseSignature = useMemo(
    () => generateEstimateCaseSignature(order),
    [order]
  );

  // Check if case signatures match
  const caseSignaturesMatch = useMemo(() => {
    if (!claim.caseSignature || !estimateCaseSignature) return null;
    return claim.caseSignature === estimateCaseSignature;
  }, [claim.caseSignature, estimateCaseSignature]);

  // Calculate claim-side percentages
  // Formula: coinsurance or OOP / (allowedAmount - deductible) * 100
  // claim.claimAmount is in cents, claimResponsibility is in cents
  const claimCoinsurancePercent = useMemo(() => {
    return calculateOOPPercent(
      claimResponsibility.coinsurance,
      claim.claimAmount,
      claimResponsibility.deductible
    );
  }, [claimResponsibility, claim.claimAmount]);

  // Calculate estimate-side percentages
  const estimateCoinsurancePercent = useMemo(() => {
    if (
      estimateValues.allowedAmount === null ||
      estimateValues.coinsurance === null ||
      estimateValues.deductible === null
    ) {
      return null;
    }
    return calculateOOPPercent(
      estimateValues.coinsurance,
      estimateValues.allowedAmount,
      estimateValues.deductible
    );
  }, [estimateValues]);

  // Calculate variances (all values now in cents)
  const variances = useMemo(() => {
    if (!hasMatch) return null;

    const allowedVariance =
      claim.expectedAmount != null
        ? claim.claimAmount - claim.expectedAmount
        : null;

    const deductibleVariance =
      estimateValues.deductible !== null
        ? claimResponsibility.deductible - estimateValues.deductible
        : null;

    const coinsuranceVariance =
      estimateValues.coinsurance !== null
        ? claimResponsibility.coinsurance - estimateValues.coinsurance
        : null;

    const oopVariance =
      estimateValues.totalOOP !== null
        ? claimResponsibility.totalOOP - estimateValues.totalOOP
        : null;

    const coinsurancePercentVariance =
      claimCoinsurancePercent !== null && estimateCoinsurancePercent !== null
        ? claimCoinsurancePercent - estimateCoinsurancePercent
        : null;

    return {
      allowedAmount: allowedVariance,
      deductible: deductibleVariance,
      coinsurance: coinsuranceVariance,
      totalOOP: oopVariance,
      coinsurancePercent: coinsurancePercentVariance,
    };
  }, [
    hasMatch,
    claimResponsibility,
    estimateValues,
    claim.claimAmount,
    claimCoinsurancePercent,
    estimateCoinsurancePercent,
  ]);

  const confidenceInfo = getConfidenceInfo(claim.orderMatchConfidence);

  if (!hasMatch) {
    return (
      <Container>
        <NoMatchMessage>
          <NoMatchIcon>
            <i className="pi pi-search" />
          </NoMatchIcon>
          <h3>No Matching Estimate Found</h3>
          <p>
            No order/estimate was found matching this claim based on patient
            name, date of service, and CPT codes.
          </p>
        </NoMatchMessage>

        <ClaimOnlySection>
          <SectionTitle>Claim Patient Responsibility</SectionTitle>
          <ComparisonTable>
            <thead>
              <tr>
                <th>Metric</th>
                <th>Claim (Actual)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Allowed Amount</td>
                <td>{formatCurrency(claim.claimAmount)}</td>
              </tr>
              <tr>
                <td>Patient Deductible</td>
                <td>{formatCurrency(claimResponsibility.deductible)}</td>
              </tr>
              <tr>
                <td>Patient Coinsurance</td>
                <td>{formatCurrency(claimResponsibility.coinsurance)}</td>
              </tr>
              <tr>
                <td>Patient Copay</td>
                <td>{formatCurrency(claimResponsibility.copay)}</td>
              </tr>
              <tr>
                <td>Total Patient OOP</td>
                <td>{formatCurrency(claimResponsibility.totalOOP)}</td>
              </tr>
              <tr>
                <td>Coinsurance %</td>
                <td>{formatPercent(claimCoinsurancePercent)}</td>
              </tr>
            </tbody>
          </ComparisonTable>
        </ClaimOnlySection>
      </Container>
    );
  }

  return (
    <Container>
      <HeaderSection>
        <MatchInfo>
          <MatchLabel>Matched Estimate:</MatchLabel>
          <Tag severity={confidenceInfo.severity}>{confidenceInfo.label}</Tag>
          {order?.dateOfService && (
            <OrderDate>DOS: {parseDate(order.dateOfService)}</OrderDate>
          )}
          {order?.id && (
            <OrderLink to={`/orders/${order.id}`} target="_blank">
              View Order
            </OrderLink>
          )}
        </MatchInfo>
      </HeaderSection>

      {claim.varianceAnalysis?.hasDiscrepancy && (
        <RootCausesSummary>
          <RootCausesHeader>
            <i className="pi pi-exclamation-triangle" />
            Detected Root Causes
          </RootCausesHeader>
          <RootCausesList>
            {formatRootCauses(claim.varianceAnalysis, claim.lineItems).map(
              (cause, idx) => (
                <RootCauseItem key={idx}>
                  {cause.label}
                  {cause.detail && (
                    <>
                      : <RootCauseDetail>{cause.detail}</RootCauseDetail>
                    </>
                  )}
                </RootCauseItem>
              )
            )}
          </RootCausesList>
        </RootCausesSummary>
      )}

      <TwoColumnLayout>
        <LeftColumn>
          <SectionTitle>Comparison: Claim vs Estimate</SectionTitle>
          <ComparisonTable>
            <thead>
              <tr>
                <th>Metric</th>
                <th>Claim (Actual)</th>
                <th>Estimate</th>
                <th>Variance</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Case Signature</td>
                <td>
                  {renderCaseSignatureWithHighlights(
                    claim.caseSignature || "",
                    estimateCaseSignature
                  )}
                </td>
                <td>
                  {renderCaseSignatureWithHighlights(
                    estimateCaseSignature,
                    claim.caseSignature || ""
                  )}
                </td>
                <VarianceCell $isNegative={caseSignaturesMatch === false}>
                  {formatCaseSignatureVariance(caseSignaturesMatch)}
                </VarianceCell>
              </tr>
              <tr>
                <td>Allowed Amount</td>
                <td>
                  {formatCurrency(claim.claimAmount)}
                  {claim.expectedAmount != null && (
                    <>
                      <br />
                      <ExpectedAmountNote>
                        (expected {formatCurrency(claim.expectedAmount)})
                      </ExpectedAmountNote>
                    </>
                  )}
                </td>
                <td>{formatCurrency(estimateValues.allowedAmount)}</td>
                <VarianceCell
                  $isPositive={
                    variances?.allowedAmount != null &&
                    variances.allowedAmount > 0
                  }
                  $isNegative={
                    variances?.allowedAmount != null &&
                    variances.allowedAmount < 0
                  }
                >
                  {variances?.allowedAmount != null
                    ? formatCurrency(variances.allowedAmount)
                    : "-"}
                </VarianceCell>
              </tr>
              <tr>
                <td>Patient Deductible</td>
                <td>{formatCurrency(claimResponsibility.deductible)}</td>
                <td>
                  {formatCurrency(estimateValues.deductible)}
                  {selectedBenefits.deductible != null && (
                    <>
                      <br />
                      <ExpectedAmountNote>
                        (selected {formatCurrency(selectedBenefits.deductible)})
                      </ExpectedAmountNote>
                    </>
                  )}
                </td>
                <VarianceCell
                  $isPositive={
                    variances?.deductible != null && variances.deductible < 0
                  }
                  $isNegative={
                    variances?.deductible != null && variances.deductible > 0
                  }
                >
                  {variances?.deductible != null
                    ? formatCurrency(variances.deductible)
                    : "-"}
                </VarianceCell>
              </tr>
              <tr>
                <td>Patient Coinsurance</td>
                <td>{formatCurrency(claimResponsibility.coinsurance)}</td>
                <td>{formatCurrency(estimateValues.coinsurance)}</td>
                <VarianceCell
                  $isPositive={
                    variances?.coinsurance != null && variances.coinsurance < 0
                  }
                  $isNegative={
                    variances?.coinsurance != null && variances.coinsurance > 0
                  }
                >
                  {variances?.coinsurance != null
                    ? formatCurrency(variances.coinsurance)
                    : "-"}
                </VarianceCell>
              </tr>
              <tr>
                <td>Patient Copay</td>
                <td>{formatCurrency(claimResponsibility.copay)}</td>
                <td>
                  {formatCurrency(estimateValues.copay)}
                  {selectedBenefits.copay != null && (
                    <>
                      <br />
                      <ExpectedAmountNote>
                        (selected {formatCurrency(selectedBenefits.copay)})
                      </ExpectedAmountNote>
                    </>
                  )}
                </td>
                <td>-</td>
              </tr>
              <tr>
                <td>Total Patient OOP</td>
                <td>{formatCurrency(claimResponsibility.totalOOP)}</td>
                <td>
                  {formatCurrency(estimateValues.totalOOP)}
                  {selectedBenefits.oopMax != null && (
                    <>
                      <br />
                      <ExpectedAmountNote>
                        (selected{" "}
                        {isNoMaximumOOP(selectedBenefits.oopMax)
                          ? "No maximum"
                          : formatCurrency(selectedBenefits.oopMax)}
                        )
                      </ExpectedAmountNote>
                    </>
                  )}
                </td>
                <VarianceCell
                  $isPositive={
                    variances?.totalOOP != null && variances.totalOOP < 0
                  }
                  $isNegative={
                    variances?.totalOOP != null && variances.totalOOP > 0
                  }
                >
                  {variances?.totalOOP != null
                    ? formatCurrency(variances.totalOOP)
                    : "-"}
                </VarianceCell>
              </tr>
              <tr>
                <td>Coinsurance %</td>
                <td>{formatPercent(claimCoinsurancePercent)}</td>
                <td>{formatPercent(estimateCoinsurancePercent)}</td>
                <VarianceCell
                  $isPositive={
                    variances?.coinsurancePercent != null &&
                    variances.coinsurancePercent < -0.05
                  }
                  $isNegative={
                    variances?.coinsurancePercent != null &&
                    variances.coinsurancePercent > 0.05
                  }
                >
                  {formatPercentVariance(variances?.coinsurancePercent)}
                </VarianceCell>
              </tr>
            </tbody>
          </ComparisonTable>
        </LeftColumn>

        <RightColumn>
          {order?.benefitDetails && (
            <BenefitsSection>
              <SectionTitle>Benefits Details (from Estimate)</SectionTitle>
              <PatientBenefits
                benefitDetailsField={order.benefitDetails}
                readOnly
              />
            </BenefitsSection>
          )}

          {!order?.benefitDetails && order?.gfeQuote && (
            <>
              <SectionTitle>Eligibility Details (from Estimate)</SectionTitle>
              <EligibilityGrid>
                <EligibilityCard>
                  <EligibilityLabel>Remaining Deductible</EligibilityLabel>
                  <EligibilityValue>
                    {formatCurrency(order.gfeQuote.remainingDeductibleInCents)}
                  </EligibilityValue>
                </EligibilityCard>
                <EligibilityCard>
                  <EligibilityLabel>Out-of-Pocket Max</EligibilityLabel>
                  <EligibilityValue>
                    {isNoMaximumOOP(order.gfeQuote.outOfPocketMaxInCents)
                      ? "No maximum"
                      : formatCurrency(order.gfeQuote.outOfPocketMaxInCents)}
                  </EligibilityValue>
                </EligibilityCard>
                <EligibilityCard>
                  <EligibilityLabel>Coinsurance %</EligibilityLabel>
                  <EligibilityValue>
                    {order.gfeQuote.coinsurancePercent !== null
                      ? `${order.gfeQuote.coinsurancePercent}%`
                      : "-"}
                  </EligibilityValue>
                </EligibilityCard>
                <EligibilityCard>
                  <EligibilityLabel>Patient Name</EligibilityLabel>
                  <EligibilityValue data-pii="patient-name">
                    {order.patient.firstName} {order.patient.lastName}
                  </EligibilityValue>
                </EligibilityCard>
              </EligibilityGrid>
            </>
          )}
        </RightColumn>
      </TwoColumnLayout>
    </Container>
  );
}

export default EstimateComparisonTab;
